/**
 * MoodEnergyWidget — Module E du dashboard
 * Humeur et énergie du jour, check-in inline si non fait
 */
import React, { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { haptic } from '../../shared/utils/haptics';

const MOODS = [
  { level: 1, emoji: '😫', label: 'Difficile' },
  { level: 2, emoji: '😔', label: 'Bas'       },
  { level: 3, emoji: '😐', label: 'Neutre'    },
  { level: 4, emoji: '🙂', label: 'Bien'      },
  { level: 5, emoji: '😊', label: 'Super'     },
];

const ENERGY_BARS = [1, 2, 3, 4];

const MOOD_STYLE = {
  1: { border: 'rgba(239,68,68,0.22)',   bg: 'rgba(239,68,68,0.07)'   },
  2: { border: 'rgba(249,115,22,0.22)',  bg: 'rgba(249,115,22,0.06)'  },
  3: { border: 'rgba(255,255,255,0.07)', bg: 'rgba(255,255,255,0.025)'},
  4: { border: 'rgba(16,185,129,0.22)',  bg: 'rgba(16,185,129,0.07)'  },
  5: { border: 'rgba(16,185,129,0.30)',  bg: 'rgba(16,185,129,0.10)'  },
};

export const MoodEnergyWidget = memo(function MoodEnergyWidget({ mood }) {
  const { todayMood, todayEnergy, hasCheckedInToday, saveCheckIn } = mood;

  const handleMoodSelect = useCallback((level) => {
    haptic.tap();
    saveCheckIn?.(level, todayEnergy || 3);
  }, [saveCheckIn, todayEnergy]);

  const handleEnergySelect = useCallback((level) => {
    haptic.tap();
    saveCheckIn?.(todayMood || 3, level);
  }, [saveCheckIn, todayMood]);

  const moodStyle = MOOD_STYLE[todayMood] || MOOD_STYLE[3];

  return (
    <div
      className="flex-1 px-3 py-3 rounded-2xl transition-colors"
      style={{ background: moodStyle.bg, border: `1px solid ${moodStyle.border}` }}
      role="region"
      aria-label="Humeur et énergie"
    >
      {!hasCheckedInToday ? (
        <>
          <p className="text-[10px] mb-2 animate-pulse tracking-wide"
            style={{ color: 'rgba(255,255,255,0.32)' }}>
            Comment tu te sens ?
          </p>
          <div className="flex items-center justify-between">
            {MOODS.map(m => (
              <motion.button
                key={m.level}
                onClick={() => handleMoodSelect(m.level)}
                className="flex flex-col items-center p-1 rounded-xl transition-colors"
                style={{ touchAction: 'manipulation' }}
                whileTap={{ scale: 0.78 }}
                whileHover={{ scale: 1.12 }}
                aria-label={m.label}
              >
                <span className="text-[24px] leading-none">{m.emoji}</span>
              </motion.button>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Humeur affichée */}
          <div className="flex items-center gap-2.5 mb-2.5">
            <span className="text-[26px] leading-none">
              {MOODS.find(m => m.level === todayMood)?.emoji || '😐'}
            </span>
            <div>
              <p className="text-[9px] uppercase tracking-wide leading-none"
                style={{ color: 'rgba(255,255,255,0.25)' }}>Humeur</p>
              <p className="text-xs font-semibold mt-0.5"
                style={{ color: 'rgba(255,255,255,0.68)' }}>
                {MOODS.find(m => m.level === todayMood)?.label || '—'}
              </p>
            </div>
          </div>

          {/* Énergie */}
          <p className="text-[9px] uppercase tracking-wide mb-1"
            style={{ color: 'rgba(255,255,255,0.25)' }}>Énergie</p>
          <div className="flex items-center gap-1">
            {ENERGY_BARS.map(bar => (
              <motion.button
                key={bar}
                onClick={() => handleEnergySelect(bar)}
                className="flex-1 h-2 rounded-full transition-all"
                style={{
                  background: bar <= (todayEnergy || 0)
                    ? `linear-gradient(90deg, #10b981, #34d399)`
                    : 'rgba(255,255,255,0.06)',
                  boxShadow: bar <= (todayEnergy || 0) ? '0 0 6px rgba(16,185,129,0.3)' : 'none',
                }}
                whileTap={{ scale: 0.85 }}
                aria-label={`Énergie ${bar}/4`}
              />
            ))}
          </div>
          <p className="text-[9px] mt-1 font-mono"
            style={{ color: 'rgba(255,255,255,0.20)' }}>
            {todayEnergy || 0}/4
          </p>
        </>
      )}
    </div>
  );
});

export default MoodEnergyWidget;
